import type { EntityKind } from '@/types/identity';
import type { TFunction } from 'i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { Event } from '@/types/event';

export enum DetectionClass {
    Person = 0,
    Child = 1,
    Cat = 2,
    Dog = 3,
    Horse = 4,
    Motorcycle = 5,
    EScooter = 6,
    Bicycle = 7,
    Micromobility = 8,
    CarVan = 9,
    Pickup = 10,
    ThreeWheeler = 11,
    VehicleLicensePlate = 12,
    Bus = 13,
    Truck = 14,
    Trailer = 15,
    RailVehicle = 16,
    Airplane = 17,
    Helicopter = 18,
    Drone = 19,
    Boat = 20,
    HeavyMachinery = 21,
    Handgun = 22,
    RifleLongGun = 23,
    Knife = 24,
    BluntWeapon = 25,
    ScreenMonitor = 26,
    Laptop = 27,
    HandheldDevice = 28,
    Camera = 29,
    Backpack = 30,
    DuffelBag = 31,
    Handbag = 32,
    Suitcase = 33,
    Bottle = 34,
    Cup = 35,
    Box = 36,
    Crate = 37,
    LongCarriedObject = 38,
    HandheldTool = 39,
    Stroller = 40,
    Wheelchair = 41,
    Fire = 42,
    Smoke = 43,
}

const CLASS_TRANSLATION_KEYS: Record<number, string> = {
    [DetectionClass.Person]: 'person',
    [DetectionClass.Child]: 'child',
    [DetectionClass.Cat]: 'cat',
    [DetectionClass.Dog]: 'dog',
    [DetectionClass.Horse]: 'horse',
    [DetectionClass.Motorcycle]: 'motorcycle',
    [DetectionClass.EScooter]: 'eScooter',
    [DetectionClass.Bicycle]: 'bicycle',
    [DetectionClass.Micromobility]: 'micromobility',
    [DetectionClass.CarVan]: 'carVan',
    [DetectionClass.Pickup]: 'pickup',
    [DetectionClass.ThreeWheeler]: 'threeWheeler',
    [DetectionClass.VehicleLicensePlate]: 'vehicleLicensePlate',
    [DetectionClass.Bus]: 'bus',
    [DetectionClass.Truck]: 'truck',
    [DetectionClass.Trailer]: 'trailer',
    [DetectionClass.RailVehicle]: 'railVehicle',
    [DetectionClass.Airplane]: 'airplane',
    [DetectionClass.Helicopter]: 'helicopter',
    [DetectionClass.Drone]: 'drone',
    [DetectionClass.Boat]: 'boat',
    [DetectionClass.HeavyMachinery]: 'heavyMachinery',
    [DetectionClass.Handgun]: 'handgun',
    [DetectionClass.RifleLongGun]: 'rifleLongGun',
    [DetectionClass.Knife]: 'knife',
    [DetectionClass.BluntWeapon]: 'bluntWeapon',
    [DetectionClass.ScreenMonitor]: 'screenMonitor',
    [DetectionClass.Laptop]: 'laptop',
    [DetectionClass.HandheldDevice]: 'handheldDevice',
    [DetectionClass.Camera]: 'camera',
    [DetectionClass.Backpack]: 'backpack',
    [DetectionClass.DuffelBag]: 'duffelBag',
    [DetectionClass.Handbag]: 'handbag',
    [DetectionClass.Suitcase]: 'suitcase',
    [DetectionClass.Bottle]: 'bottle',
    [DetectionClass.Cup]: 'cup',
    [DetectionClass.Box]: 'box',
    [DetectionClass.Crate]: 'crate',
    [DetectionClass.LongCarriedObject]: 'longCarriedObject',
    [DetectionClass.HandheldTool]: 'handheldTool',
    [DetectionClass.Stroller]: 'stroller',
    [DetectionClass.Wheelchair]: 'wheelchair',
    [DetectionClass.Fire]: 'fire',
    [DetectionClass.Smoke]: 'smoke',
};

export function getClassTranslationKey(classId: number | undefined): string {
    if (classId === undefined || classId === null || classId < 0) {
        return 'object';
    }
    return CLASS_TRANSLATION_KEYS[classId] ?? 'object';
}

export function getDetectionLabel(
    t: TFunction,
    classId: number | undefined,
): string {
    return t(`detection.${getClassTranslationKey(classId)}`, {
        ns: I18nNamespace.EVENTS,
    });
}

export type { EntityKind };

const VEHICLE_CLASS_IDS: ReadonlySet<number> = new Set([
    DetectionClass.Motorcycle,
    DetectionClass.EScooter,
    DetectionClass.Bicycle,
    DetectionClass.Micromobility,
    DetectionClass.CarVan,
    DetectionClass.Pickup,
    DetectionClass.ThreeWheeler,
    DetectionClass.Bus,
    DetectionClass.Truck,
    DetectionClass.Trailer,
]);

export function getEntityKindForClass(
    classId: number | undefined | null,
): EntityKind | null {
    if (classId === undefined || classId === null) return null;
    if (classId === DetectionClass.Person) return 'person';
    if (VEHICLE_CLASS_IDS.has(classId)) return 'vehicle';
    return null;
}

export function getAlertDescription(
    t: TFunction,
    promptText: string | undefined | null,
    classId: number | undefined,
): string {
    return promptText || getDetectionLabel(t, classId);
}

export function getEventPromptText(
    t: TFunction,
    event: Event,
    query: string | undefined | null,
): string {
    return (
        query?.trim() ||
        event.description?.trim() ||
        getDetectionLabel(t, event.classId)
    );
}
